<script setup lang="ts">
import type { ThinkingStatus, ThinkingProps, ThinkingEmits } from './types'
import { Loading, Opportunity, SuccessFilled, CircleCloseFilled, ArrowUpBold } from '@element-plus/icons-vue'

const props = withDefaults(defineProps<ThinkingProps>(), {
  content: '',
  modelValue: true,
  status: 'start' as ThinkingStatus,
  disabled: false,
  autoCollapse: false,
  buttonWidth: '160px',
  duration: '0.2s',
  maxWidth: '500px',
  backgroundColor: '#fcfcfc',
  color: 'var(--el-color-info)',
})

const emit = defineEmits<ThinkingEmits>()

const rootStyle = computed(() => ({
  '--elx-thinking-button-width': props.buttonWidth,
  '--elx-thinking-animation-duration': props.duration,
  '--elx-thinking-content-wrapper-width': props.maxWidth,
  '--elx-thinking-content-wrapper-background-color': props.backgroundColor,
  '--elx-thinking-content-wrapper-color': props.color,
}))

const isExpanded = ref(props.modelValue)

watch(() => props.modelValue, (v) => { isExpanded.value = v })

function changeExpand() {
  if (props.disabled) return
  isExpanded.value = !isExpanded.value
  emit('change', { value: isExpanded.value, status: props.status })
  emit('update:modelValue', isExpanded.value)
}

const displayedContent = computed(() => {
  return props.status === 'error' ? '思考过程中出现错误' : props.content
})

watch(() => props.status, (v) => {
  if (v === 'end' && props.autoCollapse) {
    isExpanded.value = false
    emit('update:modelValue', isExpanded.value)
  }
})
</script>

<template>
  <div class="elx-thinking" :style="rootStyle">
    <button
      :class="[
        'elx-thinking__trigger',
        `elx-thinking__trigger--${status}`,
        disabled ? 'elx-thinking__trigger--disabled' : '',
      ]"
      :disabled="disabled"
      @click="changeExpand"
    >
      <span class="elx-thinking__status-icon">
        <slot name="status-icon" :status="status">
          <el-icon v-if="status === 'thinking'" class="is-loading elx-thinking__icon-center">
            <Loading />
          </el-icon>
          <el-icon v-if="status === 'start'" class="elx-thinking__icon-center elx-thinking__start-color">
            <Opportunity />
          </el-icon>
          <el-icon v-if="status === 'end'" class="elx-thinking__icon-center elx-thinking__end-color">
            <SuccessFilled />
          </el-icon>
          <el-icon v-if="status === 'error'" class="elx-thinking__icon-center elx-thinking__error-color">
            <CircleCloseFilled />
          </el-icon>
          <el-icon v-if="status === 'cancel'" class="elx-thinking__icon-center elx-thinking__cancel-color">
            <CircleCloseFilled />
          </el-icon>
        </slot>
      </span>

      <span class="elx-thinking__label">
        <slot name="label" :status="status">
          {{
            status === 'thinking' ? '思考中...'
              : status === 'error' ? '思考遇到问题'
                : status === 'end' ? '思考完成'
                  : status === 'cancel' ? '中断思考'
                    : '开始思考'
          }}
        </slot>
      </span>

      <Transition :name="'elx-thinking-rotate'">
        <span
          v-if="!disabled"
          :class="['elx-thinking__arrow', 'elx-thinking__icon-center', isExpanded ? 'elx-thinking__arrow--expanded' : '']"
        >
          <slot name="arrow">
            <el-icon class="elx-thinking__icon-center">
              <ArrowUpBold />
            </el-icon>
          </slot>
        </span>
      </Transition>
    </button>

    <Transition :name="'elx-thinking-slide'">
      <div
        v-show="isExpanded"
        v-if="displayedContent"
        :class="['elx-thinking__content-wrapper', status === 'error' ? 'elx-thinking__content-wrapper--error-state' : '']"
      >
        <div class="elx-thinking__content">
          <slot v-if="status !== 'error'" name="content" :content="displayedContent">
            <pre>{{ displayedContent }}</pre>
          </slot>

          <slot v-else name="error" :error-content="displayedContent">
            <div class="elx-thinking__error-message">
              {{ displayedContent }}
            </div>
          </slot>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped lang="scss">
@use './style.scss';
</style>
